import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Package, Gavel } from "lucide-react";

import { useTranslation, type Language } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import LaneStatusPill from "./LaneStatusPill";
import { STATUS_SLUGS, statusTokenPrefix, type StatusSlug } from "./laneStatus";
import { statusLabel } from "./laneStatusConfig";
import { buildZoneGroups, zoneDisplay, type ZoneGroup } from "./laneGrouping";
import { laneStatusLocked, type Lane } from "./useLanes";

interface LaneTableProps {
    lanes: Lane[];
    /** Used to scope the localStorage key for collapse state. "" = All. */
    warehouseId: string;
    onLaneClick: (lane: Lane) => void;
    /** Manually change a lane's status inline (only fired when unlocked). */
    onStatusChange: (barcode: string, status: StatusSlug) => void;
}

const formatDate = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${dd}/${mm}`;
};

const collapseStorageKey = (warehouseId: string): string =>
    `lanes-collapsed-zones-${warehouseId || "all"}`;

const readCollapsed = (warehouseId: string): Set<string> => {
    try {
        const raw = window.localStorage.getItem(collapseStorageKey(warehouseId));
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr.filter((x): x is string => typeof x === "string"));
    } catch { /* ignore */ }
    return new Set();
};

const writeCollapsed = (warehouseId: string, collapsed: Set<string>) => {
    try {
        window.localStorage.setItem(
            collapseStorageKey(warehouseId),
            JSON.stringify(Array.from(collapsed)),
        );
    } catch { /* storage disabled */ }
};

const LaneTable = ({ lanes, warehouseId, onLaneClick, onStatusChange }: LaneTableProps) => {
    const { t, language } = useTranslation();
    const readOnly = useReadOnly();
    const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(warehouseId));

    // Status options, A-Z by label in the active language (matches the
    // drawer dropdown and the settings editor ordering).
    const statusOptions = useMemo(
        () => [...STATUS_SLUGS].sort((a, b) => statusLabel(a, language).localeCompare(statusLabel(b, language))),
        [language],
    );

    // Re-load collapse state when the warehouse selection changes —
    // collapse memory is scoped per warehouse so it doesn't bleed across.
    useEffect(() => {
        setCollapsed(readCollapsed(warehouseId));
    }, [warehouseId]);

    const groups = useMemo(() => buildZoneGroups(lanes), [lanes]);

    const toggle = (zoneKey: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(zoneKey)) next.delete(zoneKey);
            else next.add(zoneKey);
            writeCollapsed(warehouseId, next);
            return next;
        });
    };

    if (lanes.length === 0) {
        return <div className="lanes-empty-state">{t("lanes_empty")}</div>;
    }

    return (
        <table className="lanes-table">
            <thead>
                <tr>
                    <th className="lanes-col-zone">{t("lanes_col_zone")}</th>
                    <th className="lanes-col-lane">{t("lanes_col_lane")}</th>
                    <th className="lanes-col-status">{t("lanes_col_status")}</th>
                    <th className="lanes-col-totes">{t("lanes_col_totes")}</th>
                    <th className="lanes-col-auctions">{t("lanes_col_auctions")}</th>
                    <th className="lanes-col-end">{t("lanes_col_end")}</th>
                </tr>
            </thead>
            <tbody>
                {groups.map(g => {
                    const display = zoneDisplay(g.zone);
                    const isCollapsed = collapsed.has(g.zone);
                    return (
                        <ZoneSection
                            key={g.zone}
                            group={g}
                            zoneDisplay={display}
                            collapsed={isCollapsed}
                            onToggle={() => toggle(g.zone)}
                            onLaneClick={onLaneClick}
                            onStatusChange={onStatusChange}
                            statusOptions={statusOptions}
                            language={language}
                            t={t}
                            readOnly={readOnly}
                        />
                    );
                })}
            </tbody>
        </table>
    );
};

interface ZoneSectionProps {
    group: ZoneGroup;
    zoneDisplay: string;
    collapsed: boolean;
    onToggle: () => void;
    onLaneClick: (lane: Lane) => void;
    onStatusChange: (barcode: string, status: StatusSlug) => void;
    statusOptions: StatusSlug[];
    language: Language;
    t: (key: string) => string;
    readOnly: boolean;
}

const ZoneSection = ({ group, zoneDisplay, collapsed, onToggle, onLaneClick, onStatusChange, statusOptions, language, t, readOnly }: ZoneSectionProps) => (
    <>
        <tr className={`lanes-zone-header${collapsed ? " lanes-zone-header--collapsed" : ""}`}>
            <td colSpan={6}>
                <button
                    type="button"
                    className="lanes-zone-header-inner"
                    onClick={onToggle}
                    aria-expanded={!collapsed}
                    style={{ background: "transparent", border: "none", padding: 0 }}
                >
                    <ChevronDown size={16} strokeWidth={2} className="lanes-zone-caret" />
                    <span className="lanes-zone-name">{t("lanes_zone_label")} {zoneDisplay}</span>
                    <span className="lanes-zone-count-pill">{group.lanes.length} {t("lanes_zone_lanes_suffix")}</span>
                    {group.busy > 0 && (
                        <span className="lanes-zone-stat lanes-zone-stat--busy">
                            <span className="lanes-zone-stat-dot" />
                            {group.busy} {t("lanes_zone_busy")}
                        </span>
                    )}
                    {group.idle > 0 && (
                        <span className="lanes-zone-stat lanes-zone-stat--idle">
                            <span className="lanes-zone-stat-dot" />
                            {group.idle} {t("lanes_zone_idle")}
                        </span>
                    )}
                    {group.unprocessed > 0 && (
                        <span className="lanes-zone-stat lanes-zone-stat--unprocessed">
                            <span className="lanes-zone-stat-dot" />
                            {group.unprocessed} {t("lanes_zone_unprocessed")}
                        </span>
                    )}
                </button>
            </td>
        </tr>
        {!collapsed && group.lanes.map(lane => (
            <LaneRow
                key={lane.barcode}
                lane={lane}
                onClick={() => onLaneClick(lane)}
                onStatusChange={onStatusChange}
                statusOptions={statusOptions}
                language={language}
                t={t}
                readOnly={readOnly}
            />
        ))}
    </>
);

interface LaneRowProps {
    lane: Lane;
    onClick: () => void;
    onStatusChange: (barcode: string, status: StatusSlug) => void;
    statusOptions: StatusSlug[];
    language: Language;
    t: (key: string) => string;
    readOnly: boolean;
}

const LaneRow = ({ lane, onClick, onStatusChange, statusOptions, language, t, readOnly }: LaneRowProps) => {
    const isIdle = lane.status === "lane-empty";
    const locked = laneStatusLocked(lane);
    const prefix = statusTokenPrefix(lane.status);
    const barVar = `var(--status-${prefix}-bar)`;
    return (
        <tr
            className={`lanes-row${isIdle ? " lanes-row--idle" : ""}`}
            style={{ ["--lane-bar" as string]: barVar } as React.CSSProperties}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
            tabIndex={0}
            role="button"
        >
            <td>{lane.zone ?? "—"}</td>
            <td><span className="lanes-cell-lane">{lane.barcode}</span></td>
            <td>
                {locked ? (
                    <span title={t("lanes_status_locked_hint")}>
                        <LaneStatusPill status={lane.status} />
                    </span>
                ) : (
                    /* Stop propagation so interacting with the dropdown
                       doesn't also trigger the row's open-drawer click. */
                    <select
                        className="lanes-row-status-select"
                        value={lane.status}
                        style={{
                            background: `var(--status-${prefix}-bg)`,
                            color: `var(--status-${prefix}-fg)`,
                            borderColor: `var(--status-${prefix}-border)`,
                        }}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); onStatusChange(lane.barcode, e.target.value as StatusSlug); }}
                        aria-label={t("lanes_col_status")}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                    >
                        {statusOptions.map(slug => (
                            <option key={slug} value={slug}>{statusLabel(slug, language)}</option>
                        ))}
                    </select>
                )}
            </td>
            <td>
                <span className={`lanes-cell-numeric${lane.totes === 0 ? " lanes-cell-numeric--zero" : ""}`}>
                    <Package size={14} strokeWidth={1.8} />
                    {lane.totes === 0 ? "—" : lane.totes}
                </span>
            </td>
            <td>
                <span className={`lanes-cell-numeric${lane.auctions === 0 ? " lanes-cell-numeric--zero" : ""}`}>
                    <Gavel size={14} strokeWidth={1.8} />
                    {lane.auctions === 0 ? "—" : lane.auctions}
                </span>
            </td>
            <td className="lanes-cell-end">{formatDate(lane.auction_end_date)}</td>
        </tr>
    );
};

export default LaneTable;
